import React, { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import _uniqBy from 'lodash/uniqBy';
import _cloneDeep from 'lodash/cloneDeep';
import _map from 'lodash/map';
import { useUserContext } from '@blocs.auth';
import { AppType, graphqls, trackMediaUploadEvent, MediaHelper } from '@blocs.common';
import { helpers, useDeviceAndBrowserInfo } from '@minecraft.utils';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { gqlCountryEnum } from '@minecraft.graphql-types';
import {
  UpdateProfileMediaDoc,
  BaseMediaFragment,
  DeleteProfileMediaDoc,
  RemoveProfileResumeDoc,
  SetProfileResumeDoc,
  EditMediaDoc,
  EditMediaThumbnailDoc,
  MediaTransformation,
  RearrangeProfileMediaDoc,
  MediaTypeCodeEnum,
  AttachMultipleMediaToProfileDoc,
  AttachMediaToProfileDoc,
  SocialMediaPlatform,
  ConnectSocialMediaDoc,
  DisconnectSocialMediaDoc,
  DeleteSocialMediaDataDoc,
} from '@minecraft.graphql-operations';
import Loader from '@minecraft.loader';
import { appConfig } from '@minecraft.app-config';
import ErrorMessage from '@minecraft.error-message';
import { getEnvironment } from '@minecraft.environment';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { captureExceptionEvent } from '../Sentry';
import { useTranslation } from '../I18N';
import { useArtistEntitlement, useMediaLibrary } from '../MediaManagement';
import { CropperInstance, ReorderedMedia } from './types';
import { useInstagramAuth } from './Socials/useInstagramAuth';

const QUERY_SUBSCRIPTION_ERROR_MESSAGE = 'Observable cancelled prematurely';

const { imperialDimensionsCountries } = appConfig;
const {
  PROFILE_DETAIL,
  TALENT_PROFILE_DETAIL,
  PUBLIC_PROFILE_DETAIL,
  GET_ALL_CONFLICTS,
  GET_SELECTED_DIVISION,
  GET_ACCOUNT_DETAILS_INFO,
  GET_SUBMISSION_BY_ROLE_PROFILE,
} = graphqls.profile;

const getFormatedSubmissionMedia = (primaryMedia) => {
  return _map(primaryMedia, (media) => ({ ...media, media }));
};

const getCropperData = (cropper: CropperInstance): MediaTransformation => {
  const cropped = cropper.getData(true);

  return {
    xAxis: cropped.x,
    yAxis: cropped.y,
    width: cropped.width,
    height: cropped.height,
    rotate: cropped.rotate,
  };
};

interface ConnectorProps {
  isPublic?: boolean;
  guid?: string;
  roleId?: number;
  folderId?: number;
  showSummaryWithSubmissionDetails?: boolean;
  showSummaryWithFolderCustomization?: boolean;
  hasUrlHash?: boolean;
  appType?: AppType;
  profileId?: number;
  accountId?: number;
  selectedDivisionId?: number;
  accountCountryCode?: string;
  isPreview: boolean;
  children: (props: { fetcher: any; updater: any }) => React.ReactNode;
}

const Connector = ({
  isPublic,
  guid,
  roleId,
  folderId,
  showSummaryWithSubmissionDetails,
  showSummaryWithFolderCustomization,
  hasUrlHash,
  appType,
  profileId,
  accountId,
  accountCountryCode,
  selectedDivisionId,
  isPreview,
  children,
}: ConnectorProps) => {
  const userContext = useUserContext();
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();
  const { getPopupDimensions, onAuthCodeReceived } = useInstagramAuth();
  const getMediaText = MediaHelper.useMediaText();
  const [mutationLoading, setMutationLoading] = useState(false);
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();
  const isPublicProfileQuery = Boolean(isPublic && guid);
  const {
    data: publicProfileData,
    loading: publicProfileLoading,
    error: publicProfileError,
    refetch: publicProfileRefetch,
  } = useQuery(PUBLIC_PROFILE_DETAIL, {
    variables: {
      guid,
      roleId: roleId || 0,
      folderId: folderId || 0,
      showSummaryWithSubmissionDetails,
      showSummaryWithFolderCustomization,
    },
    fetchPolicy: hasUrlHash || isPublic ? 'cache-first' : 'cache-and-network',
    skip: !isPublicProfileQuery,
  });
  const {
    data: privateProfileData,
    loading: privateProfileLoading,
    error: privateProfileError,
    refetch: privateProfileRefetch,
  } = useQuery(appType !== AppType.Talent ? PROFILE_DETAIL : TALENT_PROFILE_DETAIL, {
    variables: { ids: profileId },
    fetchPolicy: hasUrlHash ? 'cache-first' : 'cache-and-network',
    errorPolicy: 'all',
    skip: isPublicProfileQuery,
  });
  const {
    data: submission,
    loading: submissionLoading,
    error: submissionError,
  } = useQuery(GET_SUBMISSION_BY_ROLE_PROFILE, {
    variables: {
      input: { roleId, profileId },
    },
    skip: !roleId || isPublic,
  });
  const {
    data: myAccountData,
    loading: myAccountLoading,
    error: myAccountError,
  } = useQuery(GET_ACCOUNT_DETAILS_INFO, {
    variables: { ids: accountId },
    skip: Boolean(!accountId),
  });

  const profiles = publicProfileData || privateProfileData;
  const profileData = _cloneDeep(isPublic ? profiles?.publicProfile : profiles?.profiles?.[0]);
  const artistId = profileData?.artistId;
  const account = accountId ? myAccountData?.getComplexAccountInfo : userContext?.myAccount;
  const countryCode = isPublic ? profileData?.profileAccountCountry?.code : account?.country.code;
  const artist = account ? helpers.findArtistByProfileId(account.artists, profileId) : null;

  const {
    artistMediaEntitlement,
    photoMediaLibrary,
    audioMediaLibrary,
    videoMediaLibrary,
    refetchMediaLibraryMedia,
    mediaLibraryLoading,
    mediaLibraryErr,
  } = useMediaLibrary({
    artistId: !isPreview && artistId, // only fetch media bin when in edit mode
  });
  const { availableCreditsByType, entitlementsByType } = useArtistEntitlement({ artist: artistMediaEntitlement });
  const refetchProfileData = useCallback(async () => {
    if (isPublicProfileQuery) {
      await publicProfileRefetch();
    } else {
      await privateProfileRefetch();
    }
  }, [isPublicProfileQuery, privateProfileRefetch, publicProfileRefetch]);
  const refetch = useCallback(
    async ({
      refetchProfile = true,
      refetchMedia = false,
    }: { refetchProfile?: boolean; refetchMedia?: boolean } = {}) => {
      const refetches: Promise<any>[] = [];
      if (refetchProfile) {
        refetches.push(refetchProfileData());
      }

      if (refetchMedia) {
        refetches.push(refetchMediaLibraryMedia());
      }

      await Promise.all(refetches);
    },
    [refetchProfileData, refetchMediaLibraryMedia]
  );

  const {
    data: selectedDivision,
    loading: selectedDivisionLoading,
    error: selectedDivisionError,
  } = useQuery(GET_SELECTED_DIVISION, {
    variables: { id: selectedDivisionId },
    skip: !selectedDivisionId || isPublic,
  });

  const {
    data: allConflicts,
    loading: allConflictsLoading,
    error: allConflictsError,
  } = useQuery(GET_ALL_CONFLICTS, {
    skip: appType !== AppType.Reps,
  });

  const [setResumeMutation] = useMutation(SetProfileResumeDoc);
  const [removeResumeMutation] = useMutation(RemoveProfileResumeDoc);
  const [removeProfileMediaMutation] = useMutation(DeleteProfileMediaDoc);
  // used to add multiple media to the profile
  const [addMultipleMediaToProfileMutation] = useMutation(AttachMultipleMediaToProfileDoc);
  // used to add primary media (video / audio reels) to the profile
  const [addMediaToProfileMutation] = useMutation(AttachMediaToProfileDoc);
  const [updateProfileMediaMutation] = useMutation(UpdateProfileMediaDoc);
  const [updatePhotoTransformMutation] = useMutation(EditMediaDoc);
  const [updatePhotoThumbTransformMutation] = useMutation(EditMediaThumbnailDoc);
  const [rearrangeMediaMutation] = useMutation(RearrangeProfileMediaDoc);
  const [connectSocialMedia] = useMutation(ConnectSocialMediaDoc);
  const [disconnectSocialMedia] = useMutation(DisconnectSocialMediaDoc);
  const [deleteSocialMediaData] = useMutation(DeleteSocialMediaDataDoc);

  const setResume = useCallback(
    async ({ mediaId }: { mediaId: string | number }) => {
      setMutationLoading(true);
      try {
        const res = await setResumeMutation({ variables: { mediaId: Number(mediaId), profileId: Number(profileId) } });

        if (res?.errors?.length) {
          throw res;
        }
        await refetchProfileData();
        showMessage({ color: 'success', message: t('common:profile.resumeUploadSuccess') });
        setMutationLoading(false);

        return { res };
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({ color: 'danger', message: helpers.parseErrorResponse(e) });
        setMutationLoading(false);
        return { res: e };
      }
    },
    [setResumeMutation, profileId, refetchProfileData, showMessage, t]
  );
  const removeResume = useCallback(async () => {
    setMutationLoading(true);
    try {
      const res = await removeResumeMutation({ variables: { profileId: Number(profileId) } });

      if (res?.errors?.length) {
        throw res;
      }
      await refetchProfileData();
      showMessage({ color: 'success', message: t('common:profile.resumeRemoveSuccess') });
      setMutationLoading(false);

      return { res };
    } catch (e) {
      captureExceptionEvent(e);
      showMessage({ color: 'danger', message: helpers.parseErrorResponse(e) });
      setMutationLoading(false);
      return { res: e };
    }
  }, [removeResumeMutation, profileId, refetchProfileData, showMessage, t]);

  const addProfileMedia = useCallback(
    async ({ mediaId, isPrimary }: { mediaId: number; isPrimary: boolean }) => {
      setMutationLoading(true);
      try {
        const res = await addMediaToProfileMutation({
          variables: {
            input: {
              profileId: Number(profileId),
              isPrimary,
              mediaId,
              order: isPrimary ? 0 : undefined,
              paid: false,
            },
          },
        });

        if (res?.errors?.length) {
          throw res;
        }
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({
          color: 'danger',
          message: t('common:profile.mediaBank.attachMediaFailed'),
        });
      }
      await refetch();
      setMutationLoading(false);
    },
    [addMediaToProfileMutation, profileId, refetch, showMessage, t]
  );

  const addProfileMedias = useCallback(
    async ({ medias, refetchMediaLibrary = false }: { medias: BaseMediaFragment[]; refetchMediaLibrary?: boolean }) => {
      setMutationLoading(true);
      if (!Array.isArray(medias) || !medias.length) return;

      try {
        await addMultipleMediaToProfileMutation({
          variables: {
            input: {
              profileId: Number(profileId),
              mediaIds: medias.map((media) => media.mediaId),
            },
          },
        });
        // Then track this event
        if (appType === AppType.Reps) {
          trackMediaUploadEvent({
            location: 'profile',
            organizationId: selectedDivisionId ?? -1,
            medias,
          });
        }
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({
          color: 'danger',
          message: t('common:profile.mediaBank.attachMediaFailed'),
        });
      }
      await refetch({ refetchProfile: true, refetchMedia: refetchMediaLibrary });
      setMutationLoading(false);
    },
    [refetch, addMultipleMediaToProfileMutation, profileId, appType, selectedDivisionId, showMessage, t]
  );
  const hideProfileMedia = useCallback(
    async ({ mediaId }: { mediaId: number }) => {
      setMutationLoading(true);
      try {
        const res = await removeProfileMediaMutation({ variables: { mediaId, profileId: Number(profileId) } });

        if (res?.errors?.length) throw res?.errors;
        if (!res?.data?.deleteProfileMedia) throw new Error(t('error.unexpectedServerError'));

        await refetch();
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({
          color: 'danger',
          message: t('common:profile.mediaBank.hideMediaFailed'),
        });
      }
      setMutationLoading(false);
    },
    [refetch, showMessage, t, removeProfileMediaMutation, profileId]
  );
  const updateProfileMedia = useCallback(
    async ({ profileMediaId, isPrimary }: { profileMediaId: number; isPrimary: boolean }) => {
      setMutationLoading(true);
      try {
        const res = await updateProfileMediaMutation({
          variables: {
            profileMedia: {
              isPrimary,
              profileMediaId: profileMediaId?.toString(),
            },
          },
        });
        if (res?.errors?.length) throw res?.errors;
        if (!res?.data?.updateProfileMedia) throw new Error(t('error.unexpectedServerError'));
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({
          color: 'danger',
          message: t('common:profile.mediaBank.setPrimaryFailed'),
        });
      }
      await refetch();
      setMutationLoading(false);
    },
    [updateProfileMediaMutation, refetch, showMessage, t]
  );
  const transformImage = useCallback(
    async ({ cropper, media }: { cropper: CropperInstance; media: BaseMediaFragment }) => {
      setMutationLoading(true);
      try {
        const cropperInput = getCropperData(cropper);
        const res = await updatePhotoTransformMutation({ variables: { id: media.mediaId, input: cropperInput } });
        if (res.errors?.length) {
          throw res.errors[0];
        }

        if (!res?.data?.editMedia) {
          throw new Error('failed to edit photo');
        }

        await refetch();
        showMessage({ color: 'success', message: t('common:toast.photoUpdatedSuccess') });
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({
          message: t('common:mediaHelper.photo.editFailure'),
          color: 'danger',
        });
      }
      setMutationLoading(false);
    },
    [updatePhotoTransformMutation, refetch, showMessage, t]
  );

  const transformThumbnail = useCallback(
    async ({ cropper, media }: { cropper: CropperInstance; media: BaseMediaFragment }) => {
      setMutationLoading(true);
      try {
        const cropperInput = getCropperData(cropper);
        const res = await updatePhotoThumbTransformMutation({ variables: { id: media.mediaId, input: cropperInput } });
        if (res.errors?.length) {
          throw res.errors[0];
        }

        if (!res?.data?.editMediaThumbnail) {
          throw new Error('failed to edit photo thumbnail');
        }

        await refetch();
        showMessage({ color: 'success', message: t('common:toast.photoUpdatedSuccess') });
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({
          message: t('common:mediaHelper.photo.editFailure'),
          color: 'danger',
        });
      }
      setMutationLoading(false);
    },
    [updatePhotoThumbTransformMutation, refetch, showMessage, t]
  );
  const rearrangeMedia = useCallback(
    async ({ profileMediaList, mediaType }: { profileMediaList: ReorderedMedia[]; mediaType: MediaTypeCodeEnum }) => {
      setMutationLoading(true);
      try {
        const res = await rearrangeMediaMutation({
          variables: {
            profileMediaList: profileMediaList.map((pm) => ({
              profileMediaId: pm?.profileMediaId?.toString(),
              isHidden: pm?.isHidden,
              isPrimary: pm?.isPrimary,
              order: pm?.order,
            })),
          },
        });
        if (res.errors?.length) {
          throw res.errors[0];
        }

        if (!res?.data?.updateProfileMediaList) {
          throw new Error('failed to rearrange media');
        }

        await refetch();
        showMessage({ color: 'success', message: getMediaText(mediaType, 'rearrangeSuccess') });
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({ color: 'danger', message: t('common:profile.error.failedToUpdate') });
      }
      setMutationLoading(false);
    },
    [rearrangeMediaMutation, refetch, showMessage, t, getMediaText]
  );

  const loading =
    publicProfileLoading ||
    privateProfileLoading ||
    submissionLoading ||
    myAccountLoading ||
    selectedDivisionLoading ||
    mediaLibraryLoading ||
    allConflictsLoading;
  const error =
    publicProfileError ||
    privateProfileError ||
    submissionError ||
    myAccountError ||
    selectedDivisionError ||
    mediaLibraryErr ||
    allConflictsError;

  if (loading) return <Loader />;

  // Issue with the apollo-client library, should be resolved when we upgrade the package version
  // Package: "@apollo/client"
  // GitHub Issue: https://github.com/apollographql/apollo-client/issues/7608
  if (error && error?.message !== QUERY_SUBSCRIPTION_ERROR_MESSAGE) return <ErrorMessage error={error} />;

  if (submission) {
    const submissionData = _cloneDeep(submission.getSubmissionByRoleProfile[0]);

    profileData.mediaAssets = profileData.mediaAssets.reduce((assets, asset) => {
      const submissionMedia = submissionData.media.find((el) => el.mediaId === Number(asset.media.mediaId));

      if (submissionMedia) {
        // if media included in submission we should always show it
        return assets.concat({
          ...asset,
          media: { ...asset.media, isLocked: false },
          isPrimary: submissionMedia.isPrimary,
        });
      }

      return assets;
    }, []);

    if (submissionData && submissionData.workingLocation) {
      submissionData.workingLocation.sortOrder = 0;
      // put customized as first element
      profileData.workingLocations.unshift(submissionData.workingLocation);
      // remove duplicate
      const transformedLocations = _uniqBy(profileData.workingLocations, 'id');

      profileData.workingLocations = transformedLocations.map((location, index) => {
        return { ...location, sortOrder: index };
      });
    }
  }

  if (profileData?.folderCustomization?.media) {
    profileData.mediaAssets = profileData.mediaAssets.reduce((assets, asset) => {
      const folderCustomizationMedia = profileData?.folderCustomization?.media.find(
        (el) => el.id === Number(asset.media.mediaId)
      );

      if (folderCustomizationMedia) {
        return assets.concat({
          ...asset,
          media: { ...asset.media, isLocked: false },
          isPrimary: folderCustomizationMedia.isPrimary,
        });
      }

      return assets;
    }, []);
  }

  const handleConnectSocialMediaError = () => {
    showMessage({ color: 'danger', message: t('talent:profileEdit.social.instagram.connect.error') });
  };

  const handleInstagramAuth = async (): Promise<void> => {
    try {
      setMutationLoading(true);
      const environment = getEnvironment();
      const clientId: string = environment.INSTAGRAM_CLIENT_ID;
      const ulaUrl = environment.ULA_URL;
      const redirectUri = `${ulaUrl}/instagram-auth/`;
      const url = `https://www.instagram.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=instagram_business_basic`;
      const popup: Window | null = window.open(url, 'instagram-auth', getPopupDimensions());
      const authCode: string | null = await onAuthCodeReceived(popup);
      if (!authCode) {
        throw new Error('Authentication failed');
      }

      const response = await connectSocialMedia({
        variables: { authCode, profileId, socialMediaPlatform: SocialMediaPlatform.Instagram },
        update: (cache, { data, errors }) => {
          if (errors) return;
          cache.modify({
            id: cache.identify({ __typename: 'Profile', id: profileId }),
            fields: {
              socialMedia() {
                return {
                  __typename: 'SocialMedia',
                  instagram: data?.connectSocialMedia,
                };
              },
            },
          });
        },
      });

      const { isTokenValid } = response?.data?.connectSocialMedia || {};
      if (!isTokenValid || error) {
        throw new Error('Token is not valid');
      }
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.TALENT_SOCIAL_LINK_ADD_SUCCESS,
        data: {
          socialPlatform: SocialMediaPlatform.Instagram,
          timestamp: new Date().toISOString(),
          device: deviceInfo,
          browser: browserInfo,
        },
      });
      showMessage({ color: 'success', message: t('talent:profileEdit.social.instagram.connect.successful') });
    } catch (error) {
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.TALENT_SOCIAL_LINK_ADD_ERROR,
        data: {
          socialPlatform: SocialMediaPlatform.Instagram,
          timestamp: new Date().toISOString(),
          device: deviceInfo,
          browser: browserInfo,
          errorMessage: error?.message,
        },
      });
      captureExceptionEvent('instagram account connection failed', {
        location: 'talent > profile > social consent modal',
        profileId: profileId?.toString(),
        accountId: accountId?.toString(),
        artistId: artistId?.toString(),
        errorMessage: error?.message,
        errorDescription: error?.description,
      });
      handleConnectSocialMediaError();
    } finally {
      setMutationLoading(false);
    }
  };

  const handleSocialMediaAction = async ({ action }: { action: 'disconnect' | 'delete' }) => {
    try {
      setMutationLoading(true);
      const mutation = action === 'disconnect' ? disconnectSocialMedia : deleteSocialMediaData;
      const response = await mutation({
        variables: {
          profileId,
          socialMediaPlatform: SocialMediaPlatform.Instagram,
        },
        update: (cache, { data, errors }) => {
          if ((!data?.disconnectSocialMedia?.success && !data?.deleteSocialMediaData?.success) || errors) return;
          cache.modify({
            id: cache.identify({ __typename: 'Profile', id: profileId }),
            fields: {
              socialMedia() {
                return {
                  __typename: 'SocialMedia',
                  instagram: null,
                };
              },
            },
          });
        },
      });

      let success = false;
      if (action === 'disconnect') {
        if ('disconnectSocialMedia' in response.data) {
          success = response.data.disconnectSocialMedia?.success || false;
        }
      } else if ('deleteSocialMediaData' in response.data) {
        success = response.data.deleteSocialMediaData?.success || false;
      }

      if (!success) {
        throw new Error(`Failed to ${action} social media`);
      }
      showMessage({ color: 'success', message: t('talent:profileEdit.social.instagram.disconnect.successful') });
    } catch (error) {
      captureExceptionEvent(`instagram account ${action} failed`, {
        location: `talent > profile > social ${action} modal`,
        profileId: profileId?.toString(),
        accountId: accountId?.toString(),
        artistId: artistId?.toString(),
        errorMessage: error?.message,
        errorDescription: error?.description,
      });
      showMessage({ color: 'danger', message: t('talent:profileEdit.social.instagram.connect.error') });
    } finally {
      setMutationLoading(false);
    }
  };

  const disconnectSocialAccount = () => handleSocialMediaAction({ action: 'disconnect' });
  const deleteAllSocialData = () => handleSocialMediaAction({ action: 'delete' });

  return (
    <>
      {children({
        fetcher: {
          profile: profileData,
          profileGuid: guid,
          profileRoleId: roleId,
          isUSCountry: accountCountryCode
            ? imperialDimensionsCountries.includes(accountCountryCode as gqlCountryEnum)
            : imperialDimensionsCountries.includes(countryCode),
          countryCode,
          artist,
          artistId: profileData?.artistId,
          updatedProfileField: () => undefined,
          allConflicts: allConflicts ? allConflicts.conflicts : null,
          selectedDivision: selectedDivision ? selectedDivision.organizations[0] : null,
          submissionNote:
            profileData?.submission?.note ||
            profileData?.folderCustomization?.note ||
            profileData?.defaultSubmissionNote,
          overscaleNote: profileData?.folderCustomization?.overscaleNote,
          submissionMedia:
            profileData?.folderCustomization?.media || getFormatedSubmissionMedia(profileData?.submission?.media),
          submissionRequestedMedia: profileData?.submission?.requestedMedia,
          submissionWorkingLocation: profileData?.submission?.workingLocation?.name,
          refetch,
          isFolderCustomized: !!profileData?.folderCustomization,
          photoMediaLibrary,
          audioMediaLibrary,
          videoMediaLibrary,
          availableCreditsByType,
          entitlementsByType,
        },
        updater: {
          addProfileMedias,
          addProfileMedia,
          hideProfileMedia,
          refetch,
          removeResume,
          setResume,
          updateProfileMedia,
          transformImage,
          transformThumbnail,
          rearrangeMedia,
          mutationLoading,
          handleInstagramAuth,
          disconnectSocialAccount,
          deleteAllSocialData,
        },
      })}
    </>
  );
};

export default Connector;
